/**
 * Майстер створення організації (рішення власника 2026-08-18, уточнення:
 * ПО КРОКАХ, не все на одній сторінці). Один крок = один екран:
 *   1 Базовий мінімум   — обов'язковий; кнопка «створити» РЕАЛЬНО створює
 *                         організацію (далі кроки лише ДОПОВНЮЮТЬ її);
 *   2 Подача звітів     — адреса + КАТОТТГ + орган ДПС + ОПФГ;
 *   3 Виплата зарплати  — свій IBAN + реквізити для ПДФО/ВЗ/ЄСВ;
 *   4 Повний функціонал — модулі, собівартість, декретні, шаблон, посадовці.
 * На КОЖНОМУ кроці після першого — дві дії: «продовжити заповнення» або
 * «зберегти та заповнити пізніше» (виходить на картку орг; усе, що вже
 * заповнено, збережено). Прогрес-смужка показує, де юзер зараз.
 *
 * Канон: .card + .doc-row (поля не стрибають) + DatePopover (Норма #29).
 */
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { orgs, type OrganizationCreate } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { taxOffices } from '@/api/taxOffices';
import { businessTemplates } from '@/api/admin';
import DatePopover from '@/components/DatePopover';
import KatottgPicker from '@/shared/components/KatottgPicker';
import { signControl } from '@/api/signControl';
import { findBankByIban, isValidUaIban } from '@/shared/ukr_classifiers';
import OrgNameFields, { OrgNamePreview } from '@/components/OrgNameFields';
import VatPayerSelect, {
  vatAfterTaxSystem, vatFixedFor, vatPayerProblem,
} from '@/shared/components/VatPayerSelect';
import OrgTaxAccountsTable from '@/components/OrgTaxAccountsTable';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { signalUserEdit } from '@/store/formDrafts';
import TchTable from '@/shared/components/TchTable';

// Формат-перевірки (рішення власника 2026-06-02: без checksum — реальні
// ЄДРПОУ великих компаній її не проходять).
const isValidEdrpou = (v: string) => /^\d{8}$/.test(v);
const isValidRnokpp = (v: string) => /^\d{10}$/.test(v);
const isValidKved = (v: string) => /^\d{2}\.\d{1,2}$/.test(v);

// ч.745: користувацькі податкові рядки без назви не відправляємо (бек вимагає name).
function cleanTaxRows<T extends {
  tax_accounts_extra?: OrganizationCreate['tax_accounts_extra'];
  activity_places?: OrganizationCreate['activity_places'];
}>(p: T): T {
  const out = { ...p };
  if (out.tax_accounts_extra) out.tax_accounts_extra = out.tax_accounts_extra.filter((r) => r.name.trim());
  // ч.809: місця діяльності без назви — не відправляємо.
  if (out.activity_places) out.activity_places = out.activity_places.filter((r) => r.name.trim());
  return out;
}

const EMPTY: OrganizationCreate = {
  code: null,
  name: '',
  full_name: '',
  entity_type: 'LEGAL_ENTITY',
  edrpou: null,
  inn: null,
  is_vat_payer: null,  // ч.1381: обовʼязковий вибір «так / ні»
  vat_number: null,
  vat_considered_default: true,
  prices_include_vat_default: true,
  vat_auto_default: true,
  legal_address: null,
  actual_address: null,
  phone: null,
  email: null,
  director_name: null,
  chief_accountant: null,
  cost_method: 'WAVG',
  production_costing_mode: 'SIMPLE',
  tax_system: 'GENERAL',
  maternity_calc_period_months: 12,
  fx_revaluation_period: 'QUARTERLY',
  base_currency_code: 'UAH',
  business_template_id: null,
  use_double_entry: true,
  use_chart_of_accounts: true,
  use_recipes: false,
  is_diia_city_resident: false,
  use_retail: false,
  use_directions: false,
  use_returnable_packaging: false,
  registration_date: null,
  kved_main: null,
  kved_extra: [],
  opfg_code: null,
  opfg_name: null,
  own_name: null,
  opf_short: null,
  koatuu_code: null,
  director_inn: null,
  chief_accountant_inn: null,
  sti_region: null,
  sti_district: null,
  sti_name: null,
  sti_code: null,
  tax_pdfo_recipient_name: null,
  tax_pdfo_recipient_edrpou: null,
  tax_pdfo_recipient_iban: null,
  tax_wz_recipient_name: null,
  tax_wz_recipient_edrpou: null,
  tax_wz_recipient_iban: null,
  tax_esv_recipient_name: null,
  tax_esv_recipient_edrpou: null,
  tax_esv_recipient_iban: null,
  tax_accounts_extra: [],
  activity_places: [],
  tax_purpose_templates: {},
} as OrganizationCreate;

const ALL_STEPS: { n: 1 | 2 | 3 | 4; title: string; hint: string }[] = [
  { n: 1, title: 'Базовий мінімум',
    hint: 'Без цих полів організацію не створити' },
  { n: 2, title: 'Подача звітів',
    hint: 'Адреса, КАТОТТГ, орган ДПС, додаткові місця діяльності — щоб подавати звіти у ДПС' },
  { n: 3, title: 'Виплата зарплати',
    hint: 'Ваш рахунок + реквізити для сплати ПДФО / ВЗ / ЄСВ' },
  { n: 4, title: 'Повний функціонал',
    hint: 'Модулі обліку, собівартість, керівник і головбух, контакти' },
];

/**
 * ч.1236 (рішення власника: «если хоть один счёт заполнен — можно сохранить»): що заважає
 * перейти далі з кроку 3. Досить ОДНОГО рахунку з правильним IBAN — вашого або будь-якого
 * податкового; решту заповнюють пізніше на картці. Заважає лише IBAN, набраний з помилкою
 * (його було б збережено як є), і крок, на якому не вказано жодного рахунку.
 */
export function payrollStepIssues(iban: string, form: Pick<OrganizationCreate,
  'tax_pdfo_recipient_iban' | 'tax_wz_recipient_iban' | 'tax_esv_recipient_iban'
  | 'tax_accounts_extra' | 'activity_places'>): string[] {
  const issues: string[] = [];
  let filled = 0;
  const check = (value: string | null | undefined, label: string) => {
    const v = (value ?? '').trim();
    if (!v) return;
    if (isValidUaIban(v)) filled += 1;
    else issues.push(`виправте IBAN — ${label}`);
  };
  check(iban, 'ваш рахунок');
  check(form.tax_pdfo_recipient_iban, 'ПДФО');
  check(form.tax_wz_recipient_iban, 'військовий збір');
  check(form.tax_esv_recipient_iban, 'ЄСВ');
  for (const row of form.tax_accounts_extra ?? []) check(row.recipient_iban, row.name?.trim() || 'податок без назви');
  for (const pl of form.activity_places ?? []) check(pl.pdfo_recipient_iban, `ПДФО (${pl.name})`);
  if (issues.length === 0 && filled === 0) issues.push('вкажіть хоча б один рахунок — ваш або для сплати податку');
  return issues;
}

/** Що зберігає чернетка майстра (ч.1059). */
interface WizardDraft {
  form: OrganizationCreate;
  step: 1 | 2 | 3 | 4;
  orgId: string | null;
  iban: string;
  currencyId: string;
  signControlOn: boolean;
  // ч.1060: попап «додаткове місце діяльності», заповнений до «додати».
  placeModal?: boolean;
  newPlace?: { name: string; katottg: string };
}

export default function OrganizationWizardPage() {
  const nav = useNavigate();
  // ч.809: попап «додаткове місце діяльності» (крок 2).
  const [placeModal, setPlaceModal] = useState(false);
  const [newPlace, setNewPlace] = useState({ name: '', katottg: '' });
  const qc = useQueryClient();
  const [form, setForm] = useState<OrganizationCreate>(EMPTY);
  // Поточний крок майстра: 1 базовий → 2 звіти → 3 зарплата → 4 повний.
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  // id створеної орг (після кроку 1) — далі кроки роблять PATCH.
  const [orgId, setOrgId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Банківський рахунок організації (крок «Виплата зарплати»).
  const [iban, setIban] = useState('');
  const [currencyId, setCurrencyId] = useState('');
  // 0429: контроль підписання первинки — власні endpoint'и (не поле орг).
  // У майстрі: радіо «контролювати / не контролювати»; налаштування
  // (список типів, «паперовий оригінал») — на картці орг.
  const [signControlOn, setSignControlOn] = useState(false);
  const [stiSearch, setStiSearch] = useState('');
  const [stiOpen, setStiOpen] = useState(false);

  // ч.1059: чернетка майстра (як у документах) — незбережене живе лише у
  // браузері; при поверненні на «Нову організацію» майстер пропонує
  // відновити. Разом з полями бережемо КРОК і id уже СТВОРЕНОЇ організації:
  // інакше відновлений майстер повторним «створити» зробив би дубль.
  const draftValue = useMemo<WizardDraft>(
    () => ({ form, step, orgId, iban, currencyId, signControlOn, placeModal, newPlace }),
    [form, step, orgId, iban, currencyId, signControlOn, placeModal, newPlace],
  );
  const restoreDraft = useCallback((v: WizardDraft) => {
    setForm({ ...EMPTY, ...v.form });
    setStep(v.step ?? 1);
    setOrgId(v.orgId ?? null);
    setIban(v.iban ?? '');
    setCurrencyId(v.currencyId ?? '');
    setSignControlOn(!!v.signControlOn);
    setPlaceModal(!!v.placeModal);
    setNewPlace(v.newPlace ?? { name: '', katottg: '' });
  }, []);
  const draft = useFormDraft(draftValue, restoreDraft, {
    subject: 'майстра створення організації',
    clearOnAnyMutation: false,
  });

  const currenciesQ = useQuery({ queryKey: ['currencies'], queryFn: () => refs.currencies() });
  const banksQ = useQuery({ queryKey: ['banks'], queryFn: () => refs.banks(),
    staleTime: 60 * 60 * 1000 });
  const templatesQ = useQuery({ queryKey: ['admin-templates'],
    queryFn: () => businessTemplates.list() });
  const stiQ = useQuery({
    queryKey: ['tax-offices', stiSearch],
    queryFn: () => taxOffices.list({ q: stiSearch || undefined, limit: 30 }),
    enabled: stiOpen,
    staleTime: 30 * 60 * 1000,
  });

  // UAH — валюта за замовчуванням для рахунку.
  const uahId = useMemo(
    () => currenciesQ.data?.find((c) => c.code === 'UAH')?.id ?? '',
    [currenciesQ.data]);

  const create = useMutation({
    mutationFn: async (p: OrganizationCreate) => {
      const org = await orgs.create(cleanTaxRows(p));
      if (iban.trim()) {
        try {
          await refs.createBankAccount({
            organization_id: org.id,
            iban: iban.replace(/\s+/g, '').toUpperCase(),
            currency_id: currencyId || uahId,
            account_name: null,
            is_main: true,
          });
        } catch { /* орг створена — рахунок додасться на картці */ }
      }
      return org;
    },
    onSuccess: (org) => {
      draft.markSaved();
      qc.invalidateQueries({ queryKey: ['orgs'] });
      qc.invalidateQueries({ queryKey: ['orgs-for-modules'] });
      // Організація СТВОРЕНА — далі кроки лише доповнюють її (PATCH).
      setOrgId(org.id);
      // ч.809: місця діяльності отримали id — далі PATCH оновлює, а не дублює.
      setForm((f) => ({ ...f, activity_places: org.activity_places ?? [] }));
      setStep(2);
    },
    onError: (e) => {
      const d = (e as AxiosError<{ detail?: unknown }>).response?.data?.detail;
      setError(typeof d === 'string' ? d
        : Array.isArray(d)
          ? d.map((x) => (x as { msg?: string }).msg ?? '').join('; ')
          : 'Не вдалося створити організацію');
    },
  });

  // Кроки 2-4 доповнюють ВЖЕ СТВОРЕНУ орг (PATCH). Банківський рахунок
  // створюється на кроці «Виплата зарплати» окремим викликом.
  const update = useMutation({
    mutationFn: async (p: Partial<OrganizationCreate>) => {
      if (!orgId) throw new Error('Організацію ще не створено');
      const org = await orgs.update(orgId, cleanTaxRows(p));
      if (iban.trim()) {
        try {
          await refs.createBankAccount({
            organization_id: orgId,
            iban: iban.replace(/\s+/g, '').toUpperCase(),
            currency_id: currencyId || uahId,
            account_name: null, is_main: true,
          });
          setIban('');
        } catch { /* орг збережена — рахунок додасться на картці */ }
      }
      if (signControlOn) {
        try {
          const st = await signControl.settings(orgId);
          await signControl.saveSettings(orgId, {
            enabled: true, paper_original: st.paper_original,
            types: st.types.length ? st.types : st.default_types,
          });
        } catch { /* опція вмикається і на картці орг */ }
      }
      return org;
    },
    onSuccess: (org) => {
      draft.markSaved();
      qc.invalidateQueries({ queryKey: ['orgs'] });
      qc.invalidateQueries({ queryKey: ['orgs-for-modules'] });
      setForm((f) => ({ ...f, activity_places: org.activity_places ?? [] }));
    },
    onError: (e) => {
      const d = (e as AxiosError<{ detail?: unknown }>).response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Не вдалося зберегти зміни');
    },
  });

  const busy = create.isPending || update.isPending;

  /** «продовжити заповнення» — зберегти крок і перейти на наступний. */
  const saveAndNext = () => {
    setError(null);
    update.mutate(form, { onSuccess: () => setStep((v) => (v + 1) as 2 | 3 | 4) });
  };
  /** «зберегти та заповнити пізніше» — зберегти і вийти на картку орг. */
  const saveAndExit = () => {
    setError(null);
    update.mutate(form, {
      onSuccess: () => nav(`/organizations/${orgId}`),
    });
  };

  // Крок 3: «продовжити», коли заповнено ХОЧА Б ОДИН рахунок (ч.1236, рішення власника
  // 2026-10-12; до того — рішення 2026-08-18 — вимагались усі: ваш IBAN і три податкові).
  // «зберегти та заповнити пізніше» доступна завжди.
  const payrollMissing = step === 3 ? payrollStepIssues(iban, form) : [];
  const canContinue = step !== 3 || payrollMissing.length === 0;

  const isLegal = form.entity_type === 'LEGAL_ENTITY';
  const vatFixed = vatFixedFor(form.tax_system);

  // Чого бракує для створення (крок 1) — показуємо юзеру списком.
  const missing: string[] = [];
  if (!form.opfg_code) missing.push('оргформа');
  if (!(form.own_name ?? '').trim()) missing.push(isLegal ? 'назва (без оргформи)' : 'ПІБ');
  if (isLegal && !isValidEdrpou(form.edrpou ?? '')) missing.push('ЄДРПОУ (8 цифр)');
  if (!isLegal && !isValidRnokpp(form.inn ?? '')) missing.push('РНОКПП (10 цифр)');
  if (!isLegal && !form.registration_date) missing.push('дата реєстрації');
  if (!isValidKved(form.kved_main ?? '')) missing.push('основний КВЕД');
  const vatProblem = vatPayerProblem(form);  // ч.1381
  if (vatProblem) missing.push(vatProblem);
  const canCreate = missing.length === 0 && !create.isPending;

  return (
    <div className="card" style={{ maxWidth: 980 }}>
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
        <h2 style={{ margin: 0 }}>
          {orgId ? (form.name || 'Нова організація') : 'Нова організація'}
        </h2>
        <button style={{ marginLeft: 'auto' }}
          onClick={() => nav('/organizations')}>до списку</button>
      </div>

      {/* Прогрес майстра: 4 кроки, поточний підсвічений; пройдені —
          зелені (organization вже створена і доповнюється). */}
      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap',
        margin: '0 0 0.9rem' }}>
        {ALL_STEPS.map((st) => {
          const done = st.n < step;
          const cur = st.n === step;
          return (
            <div key={st.n} style={{
              flex: '1 1 160px', padding: '0.4rem 0.6rem', borderRadius: 6,
              // ч.964: тони кроків — через змінні теми (у ночі не білі).
              border: '1px solid ' + (cur ? 'var(--primary)'
                : done ? 'var(--badge-green-bd)' : 'var(--border)'),
              background: cur ? 'var(--primary-soft)'
                : done ? 'var(--badge-green-bg)' : 'var(--bg-panel)',
              color: 'var(--text)',
              fontSize: '0.82rem',
            }}>
              <div style={{ fontWeight: cur ? 700 : 600 }}>
                {done ? '✓ ' : `${st.n}. `}{st.title}
              </div>
              {cur && (
                <div style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>
                  {st.hint}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {error && (
        <div style={{ background: 'var(--badge-red-bg)', border: '1px solid var(--badge-red-bd)',
          padding: '0.6rem',
          borderRadius: 6, marginBottom: '0.75rem', color: 'var(--badge-red-fg)' }}>
          {error}
        </div>
      )}

      {/* ── Крок 1: базовий мінімум ───────────────────────────────── */}
      {step === 1 && (<>
      <div className="doc-row">
        {/* ч.746: Оргформа (пошук) → Назва (без оргформи) → Скорочено. */}
        <OrgNameFields form={form} setForm={setForm} />
      </div>
      <OrgNamePreview form={form} />

      <div className="doc-grid" style={{ alignItems: 'flex-start' }}>
        {isLegal ? (
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>ЄДРПОУ * <small style={{ color: 'var(--muted)' }}>(8 цифр)</small></label>
            <input value={form.edrpou ?? ''} maxLength={8} placeholder="12345678"
              onChange={(e) => setForm({ ...form,
                edrpou: e.target.value.replace(/\D/g, '') || null })} />
            {form.edrpou && (
              <small style={{ color: isValidEdrpou(form.edrpou) ? 'green' : '#d33' }}>
                {isValidEdrpou(form.edrpou) ? '✓ контрольна сума' : '✗ невірний код'}
              </small>
            )}
          </div>
        ) : (<>
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>РНОКПП * <small style={{ color: 'var(--muted)' }}>(10 цифр)</small></label>
            <input value={form.inn ?? ''} maxLength={10} placeholder="1234567890"
              onChange={(e) => setForm({ ...form,
                inn: e.target.value.replace(/\D/g, '') || null })} />
            {form.inn && (
              <small style={{ color: isValidRnokpp(form.inn) ? 'green' : '#d33' }}>
                {isValidRnokpp(form.inn) ? '✓ контрольна сума' : '✗ невірний код'}
              </small>
            )}
          </div>
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>Дата реєстрації *</label>
            <DatePopover value={form.registration_date ?? ''}
              onChange={(v) => setForm({ ...form, registration_date: v || null })} />
          </div>
        </>)}
        <div className="field" style={{ gridColumn: isLegal ? 'span 9' : 'span 6' }}>
          <label>Основний КВЕД * <small style={{ color: 'var(--muted)' }}>(XX.XX)</small></label>
          <input value={form.kved_main ?? ''} placeholder="62.01" maxLength={7}
            onChange={(e) => setForm({ ...form, kved_main: e.target.value || null })} />
          {form.kved_main && (
            <small style={{ color: isValidKved(form.kved_main) ? 'green' : '#d33' }}>
              {isValidKved(form.kved_main) ? '✓ формат' : '✗ очікувано XX.XX'}
            </small>
          )}
        </div>
      </div>

      <div className="doc-grid" style={{ alignItems: 'flex-start' }}>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>Система оподаткування *</label>
          <select value={form.tax_system}
            onChange={(e) => {
              const ts = e.target.value as OrganizationCreate['tax_system'];
              const vat = vatAfterTaxSystem(form.tax_system, ts, form.is_vat_payer);
              setForm({ ...form, tax_system: ts, is_vat_payer: vat,
                vat_number: vat === false ? null : form.vat_number });
            }}>
            {isLegal ? (<>
              <option value="GENERAL">Загальна (податок на прибуток 18%)</option>
              <option value="SIMPLIFIED_3">Спрощена 3 група</option>
              <option value="SIMPLIFIED_4">Спрощена 4 (с/г)</option>
            </>) : (<>
              <option value="FOP_GENERAL">ФОП на загальній (ст. 177 ПКУ)</option>
              <option value="FOP_1">ФОП 1 група</option>
              <option value="FOP_2">ФОП 2 група</option>
              <option value="FOP_3_3">ФОП 3 група 3% (платник ПДВ)</option>
              <option value="FOP_3_5">ФОП 3 група 5%</option>
            </>)}
          </select>
        </div>
        {!(form.entity_type === 'FOP' && vatFixed === false) && (
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <VatPayerSelect value={form.is_vat_payer} fixed={vatFixed}
              onChange={(v) => setForm({ ...form, is_vat_payer: v,
                vat_number: v ? form.vat_number : null })} />
          </div>
        )}
        {form.is_vat_payer && (
          <div className="field" style={{ gridColumn: 'span 3' }}>
            <label>ІПН платника ПДВ * <small style={{ color: 'var(--muted)' }}>(9-12)</small></label>
            <input value={form.vat_number ?? ''} maxLength={12} placeholder="123456789"
              onChange={(e) => setForm({ ...form,
                vat_number: e.target.value.replace(/\D/g, '') || null })} />
          </div>
        )}
      </div>
      {form.is_vat_payer && (
        /* ч.1374: умовчання ознак ПДВ нових документів — як «Ціни та валюта → Податки» в 1С;
           у кожному документі їх можна змінити кнопкою «ціна» в таблиці рядків. */
        <div className="doc-grid" style={{ marginTop: '0.25rem' }}>
          {([
            ['vat_considered_default', 'Враховувати ПДВ',
              'Нові документи з ПДВ: у рядках є ставка і сума ПДВ. Вимкнено — документи без ПДВ (на друку «Ціна», «Сума»).'],
            ['prices_include_vat_default', 'Сума включає ПДВ',
              'Ціна рядка вже з ПДВ — сума без ПДВ виділяється з неї; на друку «Ціна з ПДВ» і «У тому числі ПДВ».'],
            ['vat_auto_default', 'Автоматичний розрахунок ПДВ',
              'Суму ПДВ рядка рахує система зі ставки. Вимкнено — суму ПДВ можна вписати вручну.'],
          ] as const).map(([key, label, hint]) => (
            <div className="field" key={key} style={{ gridColumn: 'span 4' }}>
              <label style={{ whiteSpace: 'nowrap' }} title={hint}>
                <input type="checkbox"
                  checked={form[key] ?? true}
                  onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                  style={{ width: 'auto', marginRight: '0.5rem' }} />
                {label}
                <small style={{ display: 'block', color: 'var(--muted)', whiteSpace: 'normal' }}>{hint}</small>
              </label>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end',
        marginTop: '1rem' }}>
        <button className="primary" disabled={!canCreate}
          title={canCreate ? undefined : `Заповніть: ${missing.join(', ')}`}
          onClick={() => { setError(null); create.mutate(form); }}>
          {create.isPending ? 'створення…' : 'створити і продовжити →'}
        </button>
      </div>
      {missing.length > 0 && (
        <div style={{ marginTop: '0.5rem', color: 'var(--muted)',
          fontSize: '0.85rem', textAlign: 'right' }}>
          Для створення бракує: <b>{missing.join(', ')}</b>
        </div>
      )}
      </>)}

      {/* ── Крок 2: подача звітів ────────────────────────────────── */}
      {step === 2 && (
        <div>
          <div className="doc-grid" style={{ alignItems: 'flex-start' }}>
            <div className="field" style={{ gridColumn: 'span 7' }}>
              <label>Юридична адреса</label>
              <input value={form.legal_address ?? ''}
                placeholder="вул. Хрещатик, 1, м. Київ, 01001"
                onChange={(e) => setForm({ ...form, legal_address: e.target.value || null })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>КАТОТТГ <small style={{ color: 'var(--muted)' }}>(є у витягу)</small></label>
              <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                <input value={form.koatuu_code ?? ''} maxLength={19}
                  placeholder="UA80000000000093317" style={{ flex: 1, minWidth: 0 }}
                  onChange={(e) => setForm({ ...form,
                    koatuu_code: e.target.value.toUpperCase()
                      .replace(/[^UA0-9]/g, '') || null })} />
                <KatottgPicker onPick={(code) => setForm({ ...form, koatuu_code: code })} />
              </div>
            </div>
          </div>
          <div className="doc-grid" style={{ alignItems: 'flex-start' }}>
            <div className="field" style={{ gridColumn: 'span 6', position: 'relative' }}>
              <label>Орган ДПС (ваша податкова)</label>
              <input
                value={stiOpen ? stiSearch
                  : (form.sti_name ? `${form.sti_code ?? ''} — ${form.sti_name}` : '')}
                placeholder="напр. «Львівська» або 1305"
                onFocus={() => { setStiOpen(true); setStiSearch(''); }}
                onChange={(e) => { setStiSearch(e.target.value); setStiOpen(true); }}
                onBlur={() => setTimeout(() => setStiOpen(false), 150)} />
              {stiOpen && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0,
                  zIndex: 20, background: 'var(--bg-panel)', border: '1px solid var(--border)',
                  borderRadius: 6, marginTop: 2, maxHeight: 240, overflow: 'auto',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                  {(stiQ.data?.items ?? []).map((o) => (
                    <div key={o.code}
                      onMouseDown={() => {
                        signalUserEdit();
                        setForm({ ...form, sti_code: o.code, sti_name: o.name,
                          sti_region: o.code.length >= 3
                            ? Number(o.code.slice(0, -2)) : form.sti_region });
                        setStiOpen(false);
                      }}
                      style={{ padding: '0.4rem 0.6rem', cursor: 'pointer',
                        borderBottom: '1px solid var(--border-soft)' }}>
                      <b>{o.code}</b> — {o.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {/* ч.809 (запит власника): додаткові місця діяльності (відокремлені
              підрозділи з 20-ОПП) — лише юрособи. ПДФО працівників місця — до
              місцевого бюджету за КАТОТТГ місця; реквізити рахунку — на кроці 3. */}
          {isLegal && (
            <div style={{ marginTop: '0.75rem' }}>
              {(form.activity_places ?? []).length > 0 && (
                <TchTable style={{ maxWidth: 720, marginBottom: 6 }}>
                  <thead>
                    <tr>
                      <th>Додаткове місце діяльності (назва за 20-ОПП)</th>
                      <th style={{ width: 220 }}>КАТОТТГ</th>
                      <th style={{ width: 36 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(form.activity_places ?? []).map((pl, i) => (
                      <tr key={pl.id ?? `n-${i}`}>
                        <td>{pl.name}</td>
                        <td>{pl.katottg ?? '—'}</td>
                        <td style={{ textAlign: 'center' }}>
                          <button type="button" title="прибрати" style={{ padding: '0 6px' }}
                            onClick={() => { signalUserEdit(); setForm({ ...form,
                              activity_places: (form.activity_places ?? []).filter((_, j) => j !== i) }); }}>×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </TchTable>
              )}
              <button type="button" onClick={() => { setNewPlace({ name: '', katottg: '' }); setPlaceModal(true); }}>
                + додаткове місце діяльності
              </button>
            </div>
          )}
          {placeModal && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--overlay)',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={() => setPlaceModal(false)}>
              <div className="card" style={{ width: 560, margin: 0 }} onClick={(e) => e.stopPropagation()}>
                <div className="toolbar" style={{ marginBottom: '0.5rem' }}>
                  <h3 style={{ margin: 0 }}>Додаткове місце діяльності</h3>
                  <button type="button" style={{ marginLeft: 'auto' }} onClick={() => setPlaceModal(false)}>закрити</button>
                </div>
                <div className="field">
                  <label>Назва за 20-ОПП <span style={{ color: 'red' }}>*</span></label>
                  <input value={newPlace.name} autoFocus placeholder="ЗЕМЕЛЬНА ДІЛЯНКА, МАГАЗИН…"
                    onChange={(e) => setNewPlace({ ...newPlace, name: e.target.value.toUpperCase() })} />
                </div>
                <div className="field">
                  <label>КАТОТТГ <small style={{ color: 'var(--muted)' }}>(громада, де розташоване місце)</small></label>
                  <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                    <input value={newPlace.katottg} maxLength={19} placeholder="UA…" style={{ flex: 1, minWidth: 0 }}
                      onChange={(e) => setNewPlace({ ...newPlace,
                        katottg: e.target.value.toUpperCase().replace(/[^UA0-9]/g, '') })} />
                    <KatottgPicker onPick={(code) => setNewPlace({ ...newPlace, katottg: code })} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <button type="button" className="primary" disabled={!newPlace.name.trim()}
                    onClick={() => {
                      signalUserEdit();
                      setForm({ ...form, activity_places: [...(form.activity_places ?? []), {
                        id: null, name: newPlace.name.trim(), object_type_code: null,
                        katottg: newPlace.katottg || null, address: null,
                        pdfo_recipient_name: null, pdfo_recipient_edrpou: null,
                        pdfo_recipient_iban: null, pdfo_purpose_template: null,
                      }] });
                      setPlaceModal(false);
                    }}>
                    додати
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Крок 3: виплата зарплати ─────────────────────────────── */}
      {step === 3 && (
        <div>
          <div className="doc-grid" style={{ alignItems: 'flex-start' }}>
            <div className="field" style={{ gridColumn: 'span 5' }}>
              <label>Ваш банківський рахунок (IBAN)</label>
              <input value={iban} placeholder="UA…" maxLength={34}
                title={findBankByIban(iban, banksQ.data ?? []) ?? undefined}
                style={iban && !isValidUaIban(iban) ? { borderColor: 'var(--danger)' } : undefined}
                onChange={(e) => setIban(e.target.value.replace(/[^a-zA-Z0-9]/g, '')
                  .toUpperCase().slice(0, 34))} />
            </div>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>Валюта рахунку</label>
              <select value={currencyId || uahId}
                onChange={(e) => setCurrencyId(e.target.value)}>
                {(currenciesQ.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                ))}
              </select>
            </div>
          </div>
          <OrgTaxAccountsTable form={form} setForm={setForm}
            banks={banksQ.data ?? []} style={{ marginTop: '0.75rem' }} />
          {/* ч.809: ПДФО по додаткових місцях діяльності (крок 2) — окремий рахунок
              місцевого бюджету кожного місця. */}
          {(form.activity_places ?? []).length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <b style={{ fontSize: '0.85rem' }}>ПДФО по додаткових місцях діяльності</b>
              <TchTable style={{ marginTop: 4 }}>
                <thead>
                  <tr>
                    <th style={{ width: 220 }}>Місце</th>
                    <th style={{ width: 200 }}>Отримувач ПДФО</th>
                    <th style={{ width: 110 }}>Код отримувача</th>
                    <th style={{ width: 250 }}>IBAN</th>
                  </tr>
                </thead>
                <tbody>
                  {(form.activity_places ?? []).map((pl, i) => {
                    const patch = (p: Partial<NonNullable<OrganizationCreate['activity_places']>[number]>) =>
                      setForm({ ...form, activity_places: (form.activity_places ?? [])
                        .map((r, j) => (j === i ? { ...r, ...p } : r)) });
                    return (
                      <tr key={pl.id ?? `n-${i}`}>
                        <td style={{ verticalAlign: 'middle' }}>{pl.name}{pl.katottg ? ` · ${pl.katottg}` : ''}</td>
                        <td><input value={pl.pdfo_recipient_name ?? ''}
                          onChange={(e) => patch({ pdfo_recipient_name: e.target.value || null })} /></td>
                        <td><input value={pl.pdfo_recipient_edrpou ?? ''} maxLength={10}
                          onChange={(e) => patch({ pdfo_recipient_edrpou: e.target.value.replace(/\D/g, '') || null })} /></td>
                        <td><input value={pl.pdfo_recipient_iban ?? ''} maxLength={34}
                          title={findBankByIban(pl.pdfo_recipient_iban ?? '', banksQ.data ?? []) ?? undefined}
                          style={pl.pdfo_recipient_iban && !isValidUaIban(pl.pdfo_recipient_iban) ? { borderColor: 'var(--danger)' } : undefined}
                          onChange={(e) => patch({ pdfo_recipient_iban: e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 34) || null })} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </TchTable>
            </div>
          )}
        </div>
      )}

      {/* ── Крок 4: повний функціонал ────────────────────────────── */}
      {step === 4 && (
        <div>
          <div className="doc-grid" style={{ alignItems: 'flex-start' }}>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>Телефон</label>
              <input value={form.phone ?? ''}
                onChange={(e) => setForm({ ...form, phone: e.target.value || null })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>Email</label>
              <input value={form.email ?? ''}
                onChange={(e) => setForm({ ...form, email: e.target.value || null })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>Бізнес-шаблон</label>
              <select value={form.business_template_id ?? ''}
                onChange={(e) => setForm({ ...form,
                  business_template_id: e.target.value || null })}>
                <option value="">— без шаблону —</option>
                {templatesQ.data?.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>Метод собівартості</label>
              <select value={form.cost_method}
                onChange={(e) => setForm({ ...form,
                  cost_method: e.target.value as 'FIFO' | 'WAVG' })}>
                <option value="WAVG">WAVG (середньозважена)</option>
                <option value="FIFO">FIFO</option>
              </select>
            </div>
            {/* ч.1391 (рішення власника): «Розрахунковий період для декретних» не показуємо —
                у всіх організацій 12 місяців (maternity_calc_period_months: 12 у формі). */}
          </div>
          <div style={{ marginTop: '0.4rem' }}>
            <label style={{ display: 'block', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.use_returnable_packaging ?? false}
                onChange={(e) => setForm({ ...form,
                  use_returnable_packaging: e.target.checked })}
                style={{ width: 'auto', marginRight: '0.4rem' }} />
              Облік зворотної (заставної) тари
            </label>
            {/* ч.1088: роздрібна торгівля — відкриває розділ «Роздріб» у меню. */}
            <label style={{ display: 'block', cursor: 'pointer' }}
              title="Відкриває розділ «Роздріб» у меню: торгові точки й каси, чеки, зміни, ціни, картки лояльності">
              <input type="checkbox" checked={form.use_retail ?? false}
                onChange={(e) => setForm({ ...form, use_retail: e.target.checked })}
                style={{ width: 'auto', marginRight: '0.4rem' }} />
              Роздрібна торгівля
            </label>
            <label style={{ display: 'block', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.use_recipes ?? false}
                onChange={(e) => setForm({ ...form, use_recipes: e.target.checked })}
                style={{ width: 'auto', marginRight: '0.4rem' }} />
              Облік виробництва
            </label>
            {/* Спосіб обліку — лише при увімкненому виробництві (скарга
                власника 2026-08-18: галочка була без вибору способу). */}
            {form.use_recipes && (
              <div className="field" style={{ marginLeft: '1.5rem',
                marginTop: '0.35rem', width: 520, maxWidth: '100%' }}>
                <label>Спосіб обліку виробництва</label>
                <select value={form.production_costing_mode ?? 'SIMPLE'}
                  onChange={(e) => setForm({ ...form,
                    production_costing_mode: e.target.value as 'SIMPLE' | '1C_WIP' })}>
                  <option value="SIMPLE">
                    Спрощений — матеріали одразу в собівартість (без НЗВ)
                  </option>
                  <option value="1C_WIP">
                    Повний — облік незавершеного виробництва (НЗВ), як у 1С
                  </option>
                </select>
              </div>
            )}
            {isLegal && (
              <label style={{ display: 'block', cursor: 'pointer' }}
                title="ПДФО 5%, ВЗ 5%, ЄСВ 22% від мінімальної зарплати">
                <input type="checkbox" checked={form.is_diia_city_resident ?? false}
                  onChange={(e) => setForm({ ...form,
                    is_diia_city_resident: e.target.checked })}
                  style={{ width: 'auto', marginRight: '0.4rem' }} />
                Резидент Дія.Сіті
              </label>
            )}
            {/* ч.1208: окремий норматив працевлаштування осіб з інвалідністю — 2 % замість 4 %. */}
            <label style={{ display: 'block', cursor: 'pointer' }}
              title="Заклад охорони здоров’я, реабілітаційний заклад, надавач соціальних послуг або організація, основний вид діяльності якої — реабілітація осіб з інвалідністю, їх навчання чи догляд за ними. У звіті про внесок норматив рахується як 2 % середньооблікової кількості, категорія платника — 093.">
              <input type="checkbox" checked={form.disability_norm_2pct ?? false}
                onChange={(e) => setForm({ ...form, disability_norm_2pct: e.target.checked })}
                style={{ width: 'auto', marginRight: '0.4rem' }} />
              Норматив працевлаштування осіб з інвалідністю — 2 % (охорона здоров’я, реабілітація, соціальні послуги)
            </label>
          </div>

          {/* Контроль підписання первинки (0429) — радіо у майстрі; тонке
              налаштування (список типів, «паперовий оригінал») на картці. */}
          <div style={{ border: '1px solid var(--border, #e2e8f0)',
            borderRadius: 6, padding: '0.5rem 0.7rem', marginTop: '0.7rem' }}>
            <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
              Контроль підписання первинних документів
            </div>
            <label style={{ display: 'flex', gap: '0.35rem', alignItems: 'center',
              cursor: 'pointer' }}>
              <input type="radio" checked={!signControlOn}
                onChange={() => setSignControlOn(false)} />
              не контролювати
            </label>
            <label style={{ display: 'flex', gap: '0.35rem', alignItems: 'center',
              cursor: 'pointer' }}>
              <input type="radio" checked={signControlOn}
                onChange={() => setSignControlOn(true)} />
              контролювати — система показуватиме документи без підпису
            </label>
            <small style={{ color: 'var(--muted)', display: 'block',
              marginTop: '0.25rem' }}>
              Непідписані документи підсвічуються у списках і збираються у
              розділі «ЕДО → Контроль підписання». Перелік документів під
              контролем налаштовується на картці організації.
            </small>
          </div>

          {/* Що зробити далі (рішення власника 2026-08-18). */}
          <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)',
            borderRadius: 6, padding: '0.6rem 0.75rem', marginTop: '0.7rem',
            fontSize: '0.85rem' }}>
            <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>
              Що зробити далі
            </div>
            {/* ч.1235: у ФОП звіти підписує сам підприємець — наказ не потрібен. */}
            <div style={{ marginBottom: '0.25rem' }}>
              {form.entity_type === 'FOP' ? (<>
                • <b>Звіти</b> підписує сам підприємець: ПІБ і РНОКПП для них
                уже взято з картки — наказів для цього створювати не треба.
              </>) : (<>
                • Щоб <b>подавати звіти</b> — створіть наказ про призначення
                керівника (Кадри → Накази): ПІБ і РНОКПП керівника й головного
                бухгалтера підтягнуться у звіти автоматично.
              </>)}
            </div>
            <div>
              • Щоб <b>підписувати документи</b> сканом печатки та підпису —
              завантажте їх зразки на картці організації, вкладка «Печатка
              та підпис».
            </div>
          </div>
        </div>
      )}

      {/* Дії кроків 2-4 (організація вже створена): продовжити або
          зберегти і повернутись пізніше (рішення власника 2026-08-18). */}
      {step > 1 && (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          justifyContent: 'flex-end', marginTop: '1.2rem', flexWrap: 'wrap' }}>
          <span style={{ marginRight: 'auto', color: 'var(--muted)',
            fontSize: '0.82rem' }}>
            {payrollMissing.length > 0 ? (
              <>Для переходу далі: <b>{payrollMissing.join(', ')}</b>.
              {' '}Або збережіть і поверніться пізніше.</>
            ) : 'Організацію вже створено — усе, що заповните, зберігається.'}
          </span>
          {step > 2 && (
            <button disabled={busy}
              onClick={() => setStep((v) => (v - 1) as 1 | 2 | 3)}>
              ← назад
            </button>
          )}
          <button disabled={busy} onClick={saveAndExit}>
            {busy ? 'збереження…' : 'зберегти та заповнити пізніше'}
          </button>
          {step < 4 ? (
            <button className="primary" disabled={busy || !canContinue}
              title={canContinue ? undefined
                : `Для переходу далі: ${payrollMissing.join(', ')}`}
              onClick={saveAndNext}>
              {busy ? 'збереження…' : 'продовжити заповнення →'}
            </button>
          ) : (
            <button className="primary" disabled={busy} onClick={saveAndExit}>
              {busy ? 'збереження…' : 'готово'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
